import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, ChevronLeft, ChevronRight, Plus, Trash2, Send, X, Phone, Users,
  CalendarDays, FileText, Home
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseBookingDate = (b) => new Date(`${b.pickup_date}T${b.pickup_time || "00:00"}`);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const STATUS_COLORS = {
  new: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminCalendar() {
  const navigate = useNavigate();
  const [view, setView] = useState("month"); // "day" | "week" | "month"
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [driverPanelOpen, setDriverPanelOpen] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", phone: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [bs, ds] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/drivers"),
      ]);
      setBookings(bs.data || []);
      setDrivers(ds.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_admin_token");
        navigate("/admin");
        return;
      }
      toast.error("Could not load calendar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => { localStorage.removeItem("juke_admin_token"); navigate("/admin"); };

  const shiftCursor = (dir) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  // Compute visible date range
  const range = useMemo(() => {
    if (view === "day") {
      return [new Date(cursor)];
    }
    if (view === "week") {
      const start = new Date(cursor);
      const dow = (start.getDay() + 6) % 7; // Mon = 0
      start.setDate(start.getDate() - dow);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
      });
    }
    // month
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const firstDow = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(1 - firstDow);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [cursor, view]);

  const bookingsByDay = useMemo(() => {
    const m = new Map();
    for (const b of bookings) {
      if (!b.pickup_date) continue;
      if (!m.has(b.pickup_date)) m.set(b.pickup_date, []);
      m.get(b.pickup_date).push(b);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.pickup_time || "").localeCompare(b.pickup_time || ""));
    }
    return m;
  }, [bookings]);

  const bookingsForDate = (d) => bookingsByDay.get(ymd(d)) || [];

  const headerLabel = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") return `Week of ${range[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${range[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [cursor, view, range]);

  // Driver management
  const createDriver = async () => {
    if (!newDriver.name || !newDriver.phone) return toast.error("Name and phone required.");
    try {
      const { data } = await api.post("/admin/drivers", { ...newDriver, active: true });
      setDrivers((ds) => [data, ...ds]);
      setNewDriver({ name: "", phone: "" });
      toast.success("Driver added.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add driver.");
    }
  };
  const deleteDriver = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    try {
      await api.delete(`/admin/drivers/${id}`);
      setDrivers((ds) => ds.filter((d) => d.id !== id));
      setBookings((bs) => bs.map((b) => (b.driver_id === id ? { ...b, driver_id: null } : b)));
      toast.success("Driver removed.");
    } catch { toast.error("Could not remove driver."); }
  };

  // Booking / dispatch actions
  const assign = async (bookingId, driverId) => {
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/assign`, { driver_id: driverId });
      setBookings((bs) => bs.map((b) => (b.id === bookingId ? data : b)));
      if (selected?.id === bookingId) setSelected(data);
      toast.success("Driver assigned.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed.");
    }
  };

  const dispatchWhatsApp = async (bookingId) => {
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/dispatch`);
      setBookings((bs) => bs.map((b) => (b.id === bookingId ? { ...b, dispatched_at: new Date().toISOString() } : b)));
      window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      toast.success(`Dispatched to ${data.driver_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed.");
    }
  };

  const driverById = (id) => drivers.find((d) => d.id === id);

  return (
    <div data-testid="admin-calendar" className="min-h-screen bg-[#050505] text-zinc-100">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl text-white">Juke</span>
            <span className="font-display text-xl text-[#C5A059] italic">Transfers</span>
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-zinc-500 hidden sm:inline">Dispatch</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/dashboard" data-testid="nav-dashboard" className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors">
              <Home className="w-4 h-4" /> Bookings
            </Link>
            <Link to="/admin/quotes" data-testid="nav-quotes" className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors">
              <FileText className="w-4 h-4" /> Quotes
            </Link>
            <button data-testid="drivers-btn" onClick={() => setDriverPanelOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 text-sm transition-colors">
              <Users className="w-4 h-4" /> Drivers ({drivers.length})
            </button>
            <button data-testid="refresh-btn" onClick={load} className="p-2 rounded-full hover:bg-white/5 text-zinc-400 hover:text-[#C5A059] transition-colors" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button data-testid="cal-prev" onClick={() => shiftCursor(-1)} className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059]">Today</button>
          <button data-testid="cal-next" onClick={() => shiftCursor(1)} className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059]">
            <ChevronRight className="w-4 h-4" />
          </button>
          <h1 className="ml-2 font-display text-2xl text-white capitalize">{headerLabel}</h1>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-black/50 border border-white/10 text-xs">
          {["day", "week", "month"].map((v) => (
            <button
              key={v}
              data-testid={`cal-view-${v}`}
              onClick={() => setView(v)}
              className={`h-8 px-4 rounded-full uppercase tracking-[0.2em] transition-colors ${
                view === v ? "bg-[#C5A059] text-black" : "text-zinc-400 hover:text-white"
              }`}
            >{v}</button>
          ))}
        </div>
      </div>

      {/* Calendar body */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        {view === "day" && <DayView day={range[0]} bookings={bookingsForDate(range[0])} onSelect={setSelected} driverById={driverById} />}
        {view === "week" && <WeekView days={range} bookingsForDate={bookingsForDate} onSelect={setSelected} driverById={driverById} />}
        {view === "month" && <MonthView days={range} cursor={cursor} bookingsForDate={bookingsForDate} onSelect={setSelected} />}
      </div>

      {/* Booking detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="booking-dialog" className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-white">{selected.name}</DialogTitle>
                <p className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
                  {selected.pickup_date} · {selected.pickup_time}
                  {selected.booking_type === "hourly" && ` · Hourly ${selected.duration_hours}h`}
                  {selected.is_round_trip && " · Round trip"}
                </p>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Phone" value={selected.phone} />
                <Row label="Email" value={selected.email} />
                <Row label="Pickup" value={selected.pickup_location} />
                {selected.booking_type !== "hourly" && <Row label="Drop-off" value={selected.dropoff_location} />}
                {selected.is_round_trip && <Row label="Return" value={`${selected.return_date} · ${selected.return_time}${selected.waiting_hours ? ` · wait ${selected.waiting_hours}h` : ""}`} />}
                <Row label="Passengers · Suitcases" value={`${selected.passengers} · ${selected.luggage || 0}`} />
                <Row label="Vehicle" value={selected.vehicle_type} />
                {selected.notes && <Row label="Notes" value={selected.notes} />}
                <div className={`inline-flex text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border ${STATUS_COLORS[selected.status] || STATUS_COLORS.new}`}>{selected.status}</div>
              </div>

              {/* Driver assignment */}
              <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Driver assignment</p>
                {drivers.length === 0 ? (
                  <p className="text-xs text-zinc-500">No drivers registered yet. <button onClick={() => setDriverPanelOpen(true)} className="text-[#C5A059] underline">Add one</button></p>
                ) : (
                  <select
                    data-testid="assign-driver-select"
                    value={selected.driver_id || ""}
                    onChange={(e) => assign(selected.id, e.target.value)}
                    className="w-full h-11 bg-[#0d0d0d] border border-white/10 rounded-md px-3 text-sm text-zinc-100 focus:border-[#C5A059] focus:outline-none"
                  >
                    <option value="" disabled>— Select driver —</option>
                    {drivers.filter((d) => d.active).map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>
                    ))}
                  </select>
                )}
                {selected.driver_id && (
                  <button
                    data-testid="dispatch-btn"
                    onClick={() => dispatchWhatsApp(selected.id)}
                    className="w-full flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold shadow-[0_4px_18px_rgba(37,211,102,0.35)] transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {selected.dispatched_at ? "Re-dispatch via WhatsApp" : "Dispatch via WhatsApp"}
                  </button>
                )}
                {selected.dispatched_at && (
                  <p className="text-[11px] text-zinc-500">Last dispatched: {new Date(selected.dispatched_at).toLocaleString()}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Driver management panel */}
      <Dialog open={driverPanelOpen} onOpenChange={setDriverPanelOpen}>
        <DialogContent data-testid="driver-panel" className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2"><Users className="w-5 h-5 text-[#C5A059]" /> Drivers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                data-testid="driver-name-input"
                placeholder="Driver name"
                value={newDriver.name}
                onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                className="bg-[#0d0d0d] border-white/10 focus:border-[#C5A059]"
              />
              <Input
                data-testid="driver-phone-input"
                placeholder="+385…"
                value={newDriver.phone}
                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                className="bg-[#0d0d0d] border-white/10 focus:border-[#C5A059]"
              />
            </div>
            <button
              data-testid="add-driver-btn"
              onClick={createDriver}
              className="w-full h-10 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add driver
            </button>
            <div className="border-t border-white/10 pt-4 space-y-2 max-h-80 overflow-auto">
              {drivers.length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-4">No drivers yet.</p>
              ) : drivers.map((d) => (
                <div key={d.id} data-testid={`driver-row-${d.id}`} className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/5">
                  <div>
                    <p className="text-sm text-white font-medium">{d.name}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</p>
                  </div>
                  <button
                    data-testid={`delete-driver-${d.id}`}
                    onClick={() => deleteDriver(d.id)}
                    className="p-2 rounded-full hover:bg-red-500/10 text-red-400 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-32 shrink-0 pt-0.5">{label}</p>
      <p className="text-zinc-200 flex-1">{value || "—"}</p>
    </div>
  );
}

function DayView({ day, bookings, onSelect, driverById }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-6">
      {bookings.length === 0 ? (
        <p className="text-center text-zinc-500 py-16">No bookings on this day.</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <BookingRow key={b.id} b={b} onSelect={onSelect} driverById={driverById} />
          ))}
        </div>
      )}
    </div>
  );
}

function WeekView({ days, bookingsForDate, onSelect, driverById }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
      {days.map((d) => (
        <div key={ymd(d)} data-testid={`week-day-${ymd(d)}`} className={`rounded-xl border ${sameDay(d, new Date()) ? "border-[#C5A059]/40 bg-[#C5A059]/[0.04]" : "border-white/10 bg-[#0a0a0a]"} p-4 min-h-[220px]`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
          <p className="font-display text-2xl text-white">{d.getDate()}</p>
          <div className="mt-3 space-y-2">
            {bookingsForDate(d).map((b) => (
              <MiniBooking key={b.id} b={b} onSelect={onSelect} driverById={driverById} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MonthView({ days, cursor, bookingsForDate, onSelect }) {
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] overflow-hidden">
      <div className="grid grid-cols-7 border-b border-white/10 bg-black/40">
        {dayNames.map((n) => (
          <div key={n} className="p-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center">{n}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, idx) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const items = bookingsForDate(d);
          return (
            <div
              key={idx}
              data-testid={`month-day-${ymd(d)}`}
              className={`min-h-[110px] p-2 border-b border-r border-white/5 ${!inMonth ? "opacity-30" : ""} ${sameDay(d, new Date()) ? "bg-[#C5A059]/[0.06]" : ""}`}
            >
              <p className={`text-xs mb-1 ${sameDay(d, new Date()) ? "text-[#C5A059] font-semibold" : "text-zinc-400"}`}>{d.getDate()}</p>
              <div className="space-y-1">
                {items.slice(0, 3).map((b) => (
                  <button
                    key={b.id}
                    data-testid={`month-booking-${b.id}`}
                    onClick={() => onSelect(b)}
                    className="w-full text-left px-2 py-1 rounded bg-[#C5A059]/15 border border-[#C5A059]/30 text-[10px] text-[#C5A059] truncate hover:bg-[#C5A059]/25 transition-colors"
                  >
                    {b.pickup_time} · {b.name.split(" ")[0]}
                  </button>
                ))}
                {items.length > 3 && (
                  <p className="text-[9px] text-zinc-500 pl-2">+{items.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BookingRow({ b, onSelect, driverById }) {
  const drv = b.driver_id ? driverById(b.driver_id) : null;
  return (
    <button
      onClick={() => onSelect(b)}
      data-testid={`booking-row-${b.id}`}
      className="w-full flex items-center justify-between p-4 rounded-lg bg-black/40 border border-white/5 hover:border-[#C5A059]/40 transition-colors text-left"
    >
      <div>
        <p className="font-display text-lg text-white">{b.pickup_time} · {b.name}</p>
        <p className="text-xs text-zinc-500 mt-1">
          {b.booking_type === "hourly" ? `Hourly ${b.duration_hours}h · ${b.pickup_location}` : `${b.pickup_location} → ${b.dropoff_location}`}
        </p>
        {drv && <p className="text-[11px] text-[#C5A059] mt-1">Driver: {drv.name}</p>}
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${STATUS_COLORS[b.status] || STATUS_COLORS.new}`}>{b.status}</span>
        {b.dispatched_at && <span className="text-[9px] text-emerald-400 uppercase tracking-[0.2em]">Dispatched</span>}
      </div>
    </button>
  );
}

function MiniBooking({ b, onSelect, driverById }) {
  const drv = b.driver_id ? driverById(b.driver_id) : null;
  return (
    <button
      onClick={() => onSelect(b)}
      data-testid={`week-booking-${b.id}`}
      className="w-full text-left px-2 py-1.5 rounded bg-[#C5A059]/10 border border-[#C5A059]/25 hover:bg-[#C5A059]/20 transition-colors"
    >
      <p className="text-[11px] text-[#C5A059] font-medium">{b.pickup_time} {b.name.split(" ")[0]}</p>
      <p className="text-[10px] text-zinc-500 truncate">{b.booking_type === "hourly" ? `${b.duration_hours}h charter` : b.dropoff_location}</p>
      {drv && <p className="text-[9px] text-emerald-400 truncate">→ {drv.name}</p>}
    </button>
  );
}
